import { Crosshair, MapPin } from "lucide-react";
import { useEffect, useState } from "react";
import type { PlaceCategory, PlaceStatus, QuickAddResult } from "@shared/types";
import { PLACE_CATEGORIES } from "@shared/types";
import { get } from "@/lib/api";
import { fmtDayShort, weekday } from "@/lib/format";
import { CATEGORY, PLACE_STATUS } from "@/lib/meta";
import { toast, useUI, type PlaceDraft } from "@/lib/store";
import { tripApi, type Bundle, type Derived } from "@/lib/trip";
import { SmartFill } from "../SmartFill";
import { Button, cx, Field, Input, Modal, Select, Textarea } from "../ui";

export type { PlaceDraft };

type State = PlaceDraft & { status: PlaceStatus; day: string };

const COORDS_FROM: Record<string, string> = {
  link: "координаты из ссылки",
  page: "координаты со страницы",
  geocoder: "найдено на карте по названию",
  ai: "координаты примерные — проверьте",
};

export function AddPlaceDialog({ b, d }: { b: Bundle; d: Derived }) {
  const draft = useUI((s) => s.placeDraft);
  const set = useUI((s) => s.set);
  const [v, setV] = useState<State>({ status: "want", day: "" });
  const [busy, setBusy] = useState(false);
  const [looking, setLooking] = useState(false);
  const [coordsNote, setCoordsNote] = useState<string | null>(null);
  const [existing, setExisting] = useState<string | null>(null);
  const onClose = () => set({ placeDraft: null });

  useEffect(() => {
    if (!draft) return;
    setV({ ...draft, status: "want", day: draft.day ?? useUI.getState().selectedDay ?? "" });
    setExisting(null);
    setCoordsNote(null);
    if (draft.reverse && draft.lat != null && draft.lng != null) {
      setLooking(true);
      get<{ name: string; displayName: string; category: PlaceCategory; region: string | null } | null>(`/api/geo/reverse?lat=${draft.lat}&lng=${draft.lng}`)
        .then((r) => {
          if (r) setV((x) => ({ ...x, name: x.name || r.name, address: r.displayName, category: x.category ?? r.category, region: x.region ?? r.region }));
        })
        .catch(() => undefined)
        .finally(() => setLooking(false));
    }
  }, [draft]);

  if (!draft) return null;
  const up = (p: Partial<State>) => setV((x) => ({ ...x, ...p }));
  const hasCoords = v.lat != null && v.lng != null;

  const onSmart = (r: QuickAddResult) => {
    const p = r.place;
    if (p?.existingPlaceId) {
      setExisting(p.existingPlaceId);
    }
    if (p) {
      up({
        name: p.name,
        nameLocal: p.nameLocal,
        category: p.category,
        region: p.region,
        address: p.address,
        description: p.description,
        url: p.url,
        phone: p.phone,
        ...(p.lat != null && p.lng != null ? { lat: p.lat, lng: p.lng } : {}),
      });
      setCoordsNote(p.coordsFrom ? (COORDS_FROM[p.coordsFrom] ?? null) : null);
      if (p.lat != null && p.lng != null) useUI.getState().focusMap({ center: [p.lat, p.lng], zoom: 15 });
    }
    if (r.item?.day) up({ day: r.item.day });
  };

  const save = async () => {
    if (!v.name?.trim()) return toast("Введите название", { tone: "error" });
    if (!hasCoords) return toast("Нет координат: уточните через ✨ или поставьте точку на карте", { tone: "error" });
    setBusy(true);
    try {
      const place = await tripApi.createPlace(b.trip.id, {
        name: v.name.trim(),
        nameLocal: v.nameLocal ?? null,
        lat: v.lat!,
        lng: v.lng!,
        category: v.category ?? "sight",
        status: v.status,
        region: v.region ?? null,
        address: v.address ?? null,
        description: v.description ?? null,
        url: v.url ?? null,
        phone: v.phone ?? null,
      });
      if (v.day) {
        const isStay = (v.category ?? "sight") === "hotel";
        await tripApi.createItem(b.trip.id, { day: v.day, kind: isStay ? "stay" : "visit", placeId: place.id, title: isStay ? `Ночёвка: ${place.name}` : place.name, nights: isStay ? 1 : null });
      }
      set({ addMode: false });
      useUI.getState().selectPlace(place.id);
      toast(`«${place.name}» на карте`);
      onClose();
    } catch (e) {
      toast((e as Error).message, { tone: "error" });
    } finally {
      setBusy(false);
    }
  };

  const existingPlace = existing ? d.places.get(existing) : null;

  return (
    <Modal
      open
      onClose={onClose}
      title="Новое место"
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button variant="primary" loading={busy} disabled={!hasCoords || !v.name?.trim()} onClick={save}>
            Добавить
          </Button>
        </>
      }
    >
      <SmartFill tripId={b.trip.id} want="place" day={v.day || null} initial={draft.smartText} autoRun={Boolean(draft.smartText)} onResult={onSmart} />

      {existingPlace && (
        <div className="mb-4 flex items-center gap-2 rounded-2xl border border-[var(--border)] bg-[var(--surface-2)] p-3 text-[13px]">
          <MapPin size={16} className="shrink-0 text-[var(--accent)]" />
          <span className="min-w-0 flex-1">
            Это место уже есть в поездке: <b>{existingPlace.name}</b>
          </span>
          <Button
            size="sm"
            onClick={() => {
              useUI.getState().selectPlace(existingPlace.id);
              useUI.getState().focusMap({ bounds: [[existingPlace.lat, existingPlace.lng], [existingPlace.lat, existingPlace.lng]] });
              onClose();
            }}
          >
            Открыть
          </Button>
        </div>
      )}

      <div className="grid gap-3">
        <Field
          label="Название"
          hint={
            looking ? (
              "Ищу адрес…"
            ) : hasCoords ? (
              <span>
                {v.address ? `${v.address} · ` : ""}
                {v.lat!.toFixed(5)}, {v.lng!.toFixed(5)}
                {coordsNote && <b className="ml-1">({coordsNote})</b>}
              </span>
            ) : (
              <span className="flex items-center gap-1 text-amber-700 dark:text-amber-300">
                <Crosshair size={12} /> Точка не задана — опишите место выше или нажмите на карту
              </span>
            )
          }
        >
          <Input autoFocus={!draft.smartText} value={v.name ?? ""} onChange={(e) => up({ name: e.target.value })} placeholder="Например, Phở Gia Truyền" />
        </Field>
        {v.nameLocal && (
          <Field label="Местное название">
            <Input value={v.nameLocal ?? ""} onChange={(e) => up({ nameLocal: e.target.value })} />
          </Field>
        )}
        <Field label="Категория">
          <div className="flex flex-wrap gap-1.5">
            {PLACE_CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => up({ category: c })}
                className={cx(
                  "h-8 rounded-lg border px-2 text-[13px] font-semibold",
                  (v.category ?? "sight") === c ? "border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent)]" : "border-[var(--border)]",
                )}
              >
                {CATEGORY[c].emoji} {CATEGORY[c].label}
              </button>
            ))}
          </div>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Статус">
            <Select value={v.status} onChange={(e) => up({ status: e.target.value as PlaceStatus })}>
              {(["idea", "want", "visited"] as PlaceStatus[]).map((s) => (
                <option key={s} value={s}>
                  {PLACE_STATUS[s].label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Сразу в план">
            <Select value={v.day} onChange={(e) => up({ day: e.target.value })}>
              <option value="">— не добавлять —</option>
              {d.days.map((x) => (
                <option key={x.day} value={x.day}>
                  {weekday(x.day)}, {fmtDayShort(x.day)} {x.base ? `· ${x.base}` : ""}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label="Регион / город">
          <Input value={v.region ?? ""} onChange={(e) => up({ region: e.target.value })} />
        </Field>
        <Field label="Описание">
          <Textarea value={v.description ?? ""} onChange={(e) => up({ description: e.target.value })} placeholder="Чем интересно, кто посоветовал" />
        </Field>
        {(v.url || v.phone) && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ссылка">
              <Input value={v.url ?? ""} onChange={(e) => up({ url: e.target.value })} />
            </Field>
            <Field label="Телефон">
              <Input value={v.phone ?? ""} onChange={(e) => up({ phone: e.target.value })} />
            </Field>
          </div>
        )}
      </div>
    </Modal>
  );
}
